/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { Axis, BlueprintInstanceType, Dimension, DIMENSION_HALF_LINE_TERMINATOR, DIMENSION_STROKE_WIDTH, Size, Vertex2, Vertex3 } from '@rta/rta-blueprint-library';

import { DimensionOrientationMode } from '@r-core/types';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { addContextMenu } from '../../context-menu';
import { AppContext } from '@r-app/app-context';
import { DIMENSION_FONT_SIZE, SYMBOL_ARROW_SIZE } from '@r-core/constants';
import { getDimensionMeasurement } from '@r-core/helpers';
import { renderTracking } from '@r-services/render-tracking';
import { useSvgBox } from '@r-hooks';
import { useDraggableSvg } from '@r-services/ui-interactions';

import { RenderLayer, type RenderLayerNode } from '../../services/render-layers';

const DEFAULT_STROKE_WIDTH = 0.25;

export type DrawLineProps = {
	persist?: {
		sourceId: string;
		targetId: string;
		alternateId?: string | null;
	};
	length: number;
	axis?: Axis;
	padding?: number;
	topView?: boolean;
	indicatorStart?: string;
	indicatorEnd?: string;
	labelFactor?: number;
	includeLabelBox?: boolean;
	// correct label angle, leveling it horizontally
	level?: boolean;
	canMove?: boolean;
	bBox: {
		origin: Vertex3;
		size: Size | null;
	};
	islandAngle?: number;
	// '1d' constrains to along-line only; '2d' allows along-line and perpendicular
	dragConstraint?: '1d' | '2d';
	// cumulative rotation (degrees) of parent SVG <g> transforms for drag correction
	parentRotation?: number;
	shouldHide?: boolean;
	ignoreHidden?: boolean;
	flipLabelText?: boolean;
};

export const DimensionLine = (props: DrawLineProps): React.ReactElement => {
	const { controller } = useContext(AppContext);
	const orientationMode = controller?.options?.dimensionOrientationMode ?? DimensionOrientationMode.Standard;

	renderTracking.incrementRender('DimensionLine');
	const fontSize = DIMENSION_FONT_SIZE * controller.options.dimensionFontSizeFactor;
	const { box, refText } = useSvgBox(fontSize);
	const refDimensionLine = useRef(null);

	const { dimensions } = controller.kitchen.design;
	const {
		persist = null,
		length,
		axis = new Axis(),
		indicatorStart,
		indicatorEnd,
		padding = 0,
		topView = false,
		includeLabelBox = true,
		canMove = false,
		bBox,
		level = false,
		islandAngle = 0,
		dragConstraint = '2d',
		parentRotation = 0,
		shouldHide = false,
		ignoreHidden = false,
		flipLabelText = false
	} = props;

	// Look up dimension without creating during render to avoid side effects
	const existingDimension = useMemo(() => {
		if (persist && persist.sourceId && persist.targetId) {
			return dimensions.find([persist.sourceId, persist.targetId, null], axis, false);
		}
		return null;
	}, [persist?.sourceId, persist?.targetId, axis.x, axis.y, axis.z, dimensions]);

	const [dimension, setDimension] = useState<Dimension | null>(existingDimension);

	// Defer dimension creation to useEffect to avoid state updates during render
	useEffect(() => {
		if (persist && persist.sourceId && persist.targetId) {
			let alterDim = null;
			if (persist.alternateId) {
				alterDim = dimensions.find([persist.sourceId, persist.alternateId, null], axis, false);
				if (!alterDim) alterDim = dimensions.find([persist.alternateId, persist.targetId, null], axis, false);
			}
			const dim = dimensions.find([persist.sourceId, persist.targetId, null], axis, true, { shouldHide, lineFactor: alterDim?.lineFactor ?? null, labelFactor: props.labelFactor ?? null });
			setDimension(dim);
		} else {
			setDimension(null);
		}
	}, [persist?.sourceId, persist?.targetId, persist?.alternateId, axis.x, axis.y, axis.z, shouldHide, dimensions]);

	// Use the prop-provided labelFactor as the default position; only use the dimension's
	// persisted value when the user has manually dragged it away from the 0.5 default
	const propLabel = props.labelFactor >= 0 && props.labelFactor <= 1 ? props.labelFactor : 0.5;
	const labelFactor = dimension && dimension.labelFactor !== 0.5 ? dimension.labelFactor : propLabel;
	const lineFactor = dimension ? dimension.lineFactor : 0.5;
	const [factors, setFactors] = useState({ label: labelFactor, line: lineFactor });

	// lines work in 2D space, transpose if viewing from the top
	const lineOrigin = new Vertex2({ dataIn: { x: bBox.origin.x, y: topView ? bBox.origin.z : bBox.origin.y }});
	const lineAxis = new Axis({ dataIn: { x: axis.x, y: topView ? -axis.y : axis.z }});

	// convert box size since we are in a 2D rendering space
	const boxSize: Size = bBox
		? {
			height: topView ? bBox.size.depth ?? 0 : bBox.size.height ?? 0,
			width: bBox.size.width ?? 0,
			depth: 0
		}
		: {
			height: length,
			width: length,
			depth: 0
		};

	const bBoxPadding: [number, number, number, number] = [0.5, 0.5, 0.5, 0.5];

	const enableDragging = !controller.readonly && canMove;

	// Map hook screen-space x/y factors to dimension along-line (labelFactor) / perpendicular (lineFactor)
	const lineAngleRad = (lineAxis.y * Math.PI) / 180;
	const cosLine = Math.cos(lineAngleRad);
	const sinLine = Math.sin(lineAngleRad);
	const absCos = Math.abs(cosLine);
	const absSin = Math.abs(sinLine);

	const isLineMoreVertical = absSin > absCos;
	const flipLabel = isLineMoreVertical ? sinLine < 0 : cosLine < 0;
	const flipLine = isLineMoreVertical ? sinLine > 0 : cosLine < 0;

	// Convert label/line factors to hook x/y with flip correction
	const toHookX = isLineMoreVertical
		? (flipLine ? 1 - factors.line : factors.line)
		: (flipLabel ? 1 - factors.label : factors.label);
	const toHookY = isLineMoreVertical
		? (flipLabel ? 1 - factors.label : factors.label)
		: (flipLine ? 1 - factors.line : factors.line);

	const dragPosition = {
		x: lineOrigin.x - (boxSize.width / 2) + (toHookX * boxSize.width),
		y: lineOrigin.y - (boxSize.height / 2) + (toHookY * boxSize.height)
	};

	// Hook handles all drag mechanics: mouse tracking, counter-rotation, cursor tracking
	const [hookDrag] = useDraggableSvg({
		ref: refDimensionLine,
		enabled: [enableDragging, enableDragging],
		bBox: { origin: lineOrigin, size: boxSize, padding: bBoxPadding },
		position: dragPosition,
		parentRotation,
		onDragEnd: ({ factors: dragFactors }) => {
			if (persist && dimension) {
				const rawLabelF = isLineMoreVertical ? dragFactors.new.y : dragFactors.new.x;
				const rawLineF = isLineMoreVertical ? dragFactors.new.x : dragFactors.new.y;
				const newLabel = flipLabel ? 1 - rawLabelF : rawLabelF;
				const newLine = dragConstraint === '1d' ? factors.line : (flipLine ? 1 - rawLineF : rawLineF);
				dimension.labelFactor = newLabel;
				dimension.lineFactor = newLine;
				setFactors({ label: newLabel, line: newLine });
			}
			return true;
		}
	});

	// Convert hook x/y factors back to label/line, reversing flip
	const rawLabel = isLineMoreVertical ? hookDrag.factors.y : hookDrag.factors.x;
	const rawLine = isLineMoreVertical ? hookDrag.factors.x : hookDrag.factors.y;
	const renderFactors = enableDragging ? {
		label: flipLabel ? 1 - rawLabel : rawLabel,
		line: dragConstraint === '1d' ? factors.line : (flipLine ? 1 - rawLine : rawLine)
	} : factors;

	// Sync local factors when orientation mode changes
	useEffect(() => {
		setFactors({ label: labelFactor, line: lineFactor });
	}, [orientationMode]);

	// Factors persisted to dimension only in onDragEnd to avoid @Publish on every drag frame

	if (!ignoreHidden && !controller.options.showAllDimensions && dimension && !dimension?.isVisible) return <></>;

	// short circuit if the dimension is not visible or zero
	if (controller.options.showDimensions === false || Number.isNaN(length) || length <= 0 || !refText) {
		return <></>;
	}

	// scale gap/margin values so the line doesn't collide with text at larger font sizes
	const fontScale = controller.options.dimensionFontSizeFactor / 1.2;

	const halfArrow = SYMBOL_ARROW_SIZE / 2;
	const hiddenOpacity = ignoreHidden || (dimension?.isVisible ?? true) ? 1 : 0.3;

	// text box for padded label
	const textBox = new Vertex2({ dataIn: { x: box.width, y: box.height }});

	const useGlobalLevel = orientationMode === DimensionOrientationMode.Level;

	// labelAngle computation for Standard mode and existing level prop
	let labelAngle = level
		? topView
			? bBox.origin.z < 0
				? 0
				: lineAxis.y === 180
					? lineAxis.y + 180
					: lineAxis.y + (360 - lineAxis.y)
			: 0
		: islandAngle > 0
			? islandAngle - 360
			: lineAxis.y;

	// Global Level mode: override labelAngle to counter-rotate against island group rotation
	if (useGlobalLevel) {
		labelAngle = islandAngle;
	}

	// Apply customAngle from dimension object (per-dimension rotation, not yet in published types)
	const customAngle = (dimension as unknown as { customAngle?: number | null })?.customAngle;
	if (customAngle != null) {
		labelAngle += customAngle;
	}

	// Diagonal island label correction (skipped in global Level mode)
	if (!useGlobalLevel && islandAngle % 10 === 5) {
		labelAngle = ((islandAngle - (islandAngle / 2)) * 2) + 90;
		if (length > 40) labelAngle += 180;
		if (islandAngle === 45 || islandAngle === 225) labelAngle += islandAngle * 4;
	}

	if (flipLabelText && !useGlobalLevel) labelAngle += islandAngle * 2;

	// Normalize labelAngle to 0-359 range to avoid edge cases with 360 degree rotation
	labelAngle = ((labelAngle % 360) + 360) % 360;

	// textBox rotation: only for original level prop, not global Level
	if (level && !useGlobalLevel) textBox.rotateAxis(new Vertex2(), labelAngle);

	const boxForLineLabel = textBox.clone().rotateAxis(new Vertex2(), (level && !useGlobalLevel) ? lineAxis.y : 0);

	const halfLength = length / 2;
	let halfLineLabelWidth = Math.abs(boxForLineLabel.x) / 2;

	// Calculate the projected width of the rotated label onto the line direction
	// This ensures the gap properly accounts for the label bounds at any rotation angle
	const labelRotationRelativeToLine = ((labelAngle - lineAxis.y) % 360) * Math.PI / 180;
	const projectedHalfWidth = ((Math.abs(box.width * Math.cos(labelRotationRelativeToLine)) +
		Math.abs(box.height * Math.sin(labelRotationRelativeToLine))) / 2) + (0.5 * fontScale);

	// Use the larger value to ensure the gap is always wide enough for the rotated label
	if (projectedHalfWidth > halfLineLabelWidth) {
		halfLineLabelWidth = projectedHalfWidth;
	}

	// margin is applied to the left and right side to allow for line terminators
	const startSymbolOffset = indicatorStart === 'arrow' ? SYMBOL_ARROW_SIZE : 0;
	const endSymbolOffset = indicatorEnd === 'arrow' ? SYMBOL_ARROW_SIZE : 0;

	// margins for the label box -- scale with font so the bubble grows with text
	let marginLeft = 0.1 * fontScale;
	let marginRight = 0.1 * fontScale;
	const boxForLineLabelLength = (halfLength * 0.7 < halfLineLabelWidth ? boxForLineLabel.y : boxForLineLabel.x) ?? 0;

	// label position -- use a reduced effective label length so lines can extend closer to the text
	const effectiveLabelLength = boxForLineLabelLength * 0.7;
	const availableLength = length - effectiveLabelLength - marginLeft - marginRight - startSymbolOffset - endSymbolOffset;
	const showLines = availableLength > -1;
	const clampedAvailable = Math.max(availableLength, 0);
	let labelCenterOffset = (clampedAvailable * renderFactors.label) - (clampedAvailable / 2);

	// padding moves the origin
	const turnAxis = lineAxis.y;
	let lineOffset = 0;
	if (lineAxis.y === 0) {
		lineOffset = -((bBox.size.height * renderFactors.line) - (bBox.size.height / 2)) / 2;
	} else {
		lineOffset = ((bBox.size.width * renderFactors.line) - (bBox.size.width / 2)) / 2;
	}
	const dimOrigin = lineOrigin
		.clone()
		.moveOnAxis(padding, lineAxis.y + 90)
		.moveOnAxis(lineOffset, lineAxis.y === 0 ? lineAxis.y - 90 : turnAxis + 90)
		.moveOnAxis(lineOffset, lineAxis.y === 0 ? lineAxis.y - 90 : turnAxis + 90);
	const labelOrigin = dimOrigin.clone().moveOnAxis(labelCenterOffset, lineAxis.y);

	const lineSegmentVertical = [dimOrigin.clone().moveOnAxis(halfLength, lineAxis.y), dimOrigin.clone().moveOnAxis(halfLength, lineAxis.y - 180)];

	if (!useGlobalLevel && halfLength * 0.7 < halfLineLabelWidth && lineSegmentVertical[0].x - lineSegmentVertical[1].x > 0) {
		// Compose with customAngle instead of clobbering it, so "Rotate 90" still has an effect on short lines
		labelAngle = 90 + (customAngle ?? 0);
		halfLineLabelWidth = boxForLineLabelLength / 2;
	}

	let translateOrigin = 0;
	if (!useGlobalLevel && halfLineLabelWidth + (0.5 * fontScale) >= halfLength && lineSegmentVertical[0].x - lineSegmentVertical[1].x > 0) {
		translateOrigin = (boxForLineLabel.x / -2) - 1;
		halfLineLabelWidth = 0;
		marginLeft = 0;
		marginRight = 0;
		labelCenterOffset = halfLength;
	}

	const line1Segment = [
		dimOrigin.clone().moveOnAxis(halfLength - startSymbolOffset, lineAxis.y),
		dimOrigin.clone().moveOnAxis(Math.min(labelCenterOffset + marginLeft + halfLineLabelWidth, halfLength), lineAxis.y)
	];

	const line2Segment = [
		dimOrigin.clone().moveOnAxis(Math.min(-labelCenterOffset + marginRight + halfLineLabelWidth, halfLength), lineAxis.y - 180),
		dimOrigin.clone().moveOnAxis(halfLength - endSymbolOffset, lineAxis.y - 180)
	];

	const line1Props = {
		x1: line1Segment[0].x,
		x2: line1Segment[1].x,
		y1: line1Segment[0].y,
		y2: line1Segment[1].y
	};

	const line2Props = {
		x1: line2Segment[0].x,
		x2: line2Segment[1].x,
		y1: line2Segment[0].y,
		y2: line2Segment[1].y
	};

	// Compute label box origin from labelOrigin to avoid one-frame getBBox jitter during drag
	const labelBoxX = labelOrigin.x - (box.width / 2);
	const labelBoxY = labelOrigin.y - (box.height / 2);

	// Use absolute values since customAngle rotation can produce negative textBox components
	if (Math.abs(textBox.x) + marginLeft + marginRight < 0 || Math.abs(textBox.y) < 0) return <></>;

	return (
		<g
			{...addContextMenu({
				menuKey: BlueprintInstanceType.Dimension,
				id: dimension?.id ?? null
			})}
		>
			{showLines && <g>
				{/* use arrows as terminators */}
				{indicatorStart === 'arrow' && (
					<use
						xlinkHref="#right-arrow"
						opacity={hiddenOpacity}
						transform={`rotate(${lineAxis.y} ${line1Segment[0].x} ${line1Segment[0].y})`}
						x={line1Segment[0].x}
						y={line1Segment[0].y - halfArrow}
					/>
				)}
				{indicatorEnd === 'arrow' && (
					<use
						xlinkHref="#left-arrow"
						opacity={hiddenOpacity}
						transform={`rotate(${lineAxis.y} ${line2Segment[1].x} ${line2Segment[1].y})`}
						x={line2Segment[1].x - endSymbolOffset}
						y={line2Segment[1].y - halfArrow}
					/>
				)}

				{/* use lines as terminators */}
				{indicatorStart === 'flat' && (
					<line
						opacity={hiddenOpacity}
						x1={line1Props.x1}
						y1={line1Props.y1 - DIMENSION_HALF_LINE_TERMINATOR}
						x2={line1Props.x1}
						y2={line1Props.y1 + DIMENSION_HALF_LINE_TERMINATOR}
						transform={`rotate(${lineAxis.y} ${line1Props.x1} ${line1Props.y1})`}
						stroke="black"
						strokeWidth={DIMENSION_STROKE_WIDTH}
					/>
				)}
				{indicatorEnd === 'flat' && (
					<line
						opacity={hiddenOpacity}
						x1={line2Props.x2}
						y1={line2Props.y2 - DIMENSION_HALF_LINE_TERMINATOR}
						x2={line2Props.x2}
						y2={line2Props.y2 + DIMENSION_HALF_LINE_TERMINATOR}
						transform={`rotate(${lineAxis.y} ${line2Props.x2} ${line2Props.y2})`}
						stroke="black"
						strokeWidth={DIMENSION_STROKE_WIDTH}
					/>
				)}

				<line {...line1Props} stroke="white" fill="white" strokeWidth={DEFAULT_STROKE_WIDTH * 1.2} opacity={hiddenOpacity} />
				<line {...line1Props} stroke="black" strokeWidth={DEFAULT_STROKE_WIDTH} opacity={hiddenOpacity} />

				<line {...line2Props} stroke="white" fill="white" strokeWidth={DEFAULT_STROKE_WIDTH * 1.2} opacity={hiddenOpacity} />
				<line {...line2Props} stroke="black" strokeWidth={DEFAULT_STROKE_WIDTH} opacity={hiddenOpacity} />
			</g>}

			{/* ----------------------------------------- */}
			{/* Draw the Label Part of the Dimension Line */}
			{/* ----------------------------------------- */}

			<g>
				<g 	id={dimension?.id ?? null}
					ref={refDimensionLine} transform={`translate(0, ${translateOrigin})rotate(${labelAngle} ${labelOrigin.x} ${labelOrigin.y})`}>
					{/* developer insights */}
					{controller.options.showDeveloperInsights && <rect x={labelBoxX} y={labelBoxY} width={box.width} height={box.height} fill="grey" />}

					{/* draw the label box */}
					<rect
						stroke={includeLabelBox ? 'black' : 'none'}
						strokeWidth={DEFAULT_STROKE_WIDTH / 3}
						x={labelBoxX - marginLeft}
						y={labelBoxY}
						width={Math.abs(textBox.x) + marginLeft + marginRight}
						height={Math.abs(textBox.y)}
						fill={includeLabelBox ? 'white' : 'none'}
						rx="0.5"
						ry="0.75"
						opacity={0.7}
					/>

					{/* Draw the dimension text (rotated around labelOrigin to share center with labelAngle) */}
					<text
						ref={refText}
						opacity={hiddenOpacity}
						transform={`rotate(${!useGlobalLevel && lineAxis.y > 90 && lineAxis.y < 270 ? 180 - (lineAxis.y % 45) : 0} ${labelOrigin.x} ${labelOrigin.y})`}
						textAnchor="middle"
						dominantBaseline="middle"
						fill="black"
						fontSize={fontSize}
						x={labelOrigin.x}
						y={labelOrigin.y}
						className="rta-designer-component-measure rta-noselect"
					>
						{getDimensionMeasurement(length, controller.options.useFractions)}&quot;
					</text>
				</g>
			</g>
		</g>
	);
};

export const dimensionLineLayer = (props: DrawLineProps): RenderLayerNode => {
	return {
		id: '111',
		priority: 0,
		layer: RenderLayer.Dimension,
		element: <DimensionLine {...props} />
	};
};
